import React, { useState } from 'react'
import { Button, Tooltip } from 'antd'
import { EditOutlined, LockOutlined, MailOutlined, UnlockOutlined } from '@ant-design/icons'
import type { Member, MemberTableRow, Organization, OrgRole } from '../../types'
import { organizationHasPaidSeatLimit } from '../../utils'
import OrgChipList from '../shared/OrgChipList'
import MemberStatusTag from '../shared/MemberStatusTag'
import AccountTierTag from '../shared/AccountTierTag'

interface MemberRowProps {
  row: MemberTableRow
  organization: Organization
  onAssign?: (member: Member, organization: Organization) => void
  onEdit?: (member: Member, organization: Organization) => void
  onDisable?: (member: Member, organization: Organization) => void
  onEnable?: (member: Member, organization: Organization) => void
  onResendInvite?: (member: Member, organization: Organization) => Promise<void>
}

const memberRolesInOrg = (row: MemberTableRow, organization: Organization): OrgRole[] =>
  row.member.roleLists?.[organization.id] || [row.member.roles[organization.id]]

const isSuperAdminManager = (organization: Organization) =>
  organization.myRole === 'Super Admin' || organization.roles.includes('SuperAdmin')

const MemberRow: React.FC<MemberRowProps> = ({ row, organization, onAssign, onEdit, onDisable, onEnable, onResendInvite }) => {
  const [resending, setResending] = useState(false)
  const disabled = row.member.status === 'Disabled'
  const roles = memberRolesInOrg(row, organization)
  const targetIsSuperAdmin = roles.some((role) => role === 'Super Admin')
  const targetIsOwner = roles.some((role) => role === 'Owner')
  const managerIsSuperAdmin = isSuperAdminManager(organization)
  const disableDisabled = targetIsSuperAdmin || (targetIsOwner && !managerIsSuperAdmin)
  const editDisabled = disabled
  const editTooltip = editDisabled ? 'Disabled accounts cannot be edited' : 'Edit user'
  const disableTooltip = targetIsSuperAdmin
    ? 'Super Admin accounts cannot be disabled here'
    : targetIsOwner && !managerIsSuperAdmin
      ? 'Owner accounts cannot be disabled here'
      : 'Disable account'
  const accessTooltip = disabled ? 'Enable account' : disableTooltip

  return (
    <div className="um-refactor__row">
      <div className="um-refactor__cell-sticky um-refactor__cell-sticky--email">
        <div className="um-refactor__cell-email">{row.member.email}</div>
      </div>
      <div className="um-refactor__cell-sticky um-refactor__cell-sticky--name">
        <div className="um-refactor__cell-name">{row.member.name}</div>
      </div>
      <OrgChipList chips={row.roleChips} />
      <div className="um-refactor__cell-muted">{row.member.nmls}</div>
      <OrgChipList chips={row.orgChips} />
      <div>
        <MemberStatusTag status={row.member.status} />
      </div>
      <div>
        <AccountTierTag
          tier={row.member.accountTier}
          disabled={disabled}
          occupiesSeat={organizationHasPaidSeatLimit(organization)}
        />
      </div>
      <div className="um-refactor__actions">
        {onAssign && (
          <Button type="link" className="um-refactor__action-link" onClick={() => onAssign(row.member, organization)}>
            Manage orgs
          </Button>
        )}
        {onResendInvite && row.member.status === 'Invited' && (
          <Tooltip title="Resend invite">
            <Button
              type="text"
              loading={resending}
              icon={<MailOutlined rev={undefined} />}
              className="um-refactor__action-icon"
              onClick={async () => {
                setResending(true)
                try {
                  await onResendInvite(row.member, organization)
                } finally {
                  setResending(false)
                }
              }}
            />
          </Tooltip>
        )}
        <Tooltip title={editTooltip}>
          <Button
            type="text"
            disabled={editDisabled}
            icon={<EditOutlined rev={undefined} />}
            className="um-refactor__action-icon"
            onClick={() => onEdit?.(row.member, organization)}
          />
        </Tooltip>
        {disabled ? (
          <Tooltip title={accessTooltip}>
            <Button
              type="text"
              icon={<UnlockOutlined rev={undefined} />}
              className="um-refactor__action-icon um-refactor__action-icon--success"
              onClick={() => onEnable?.(row.member, organization)}
            />
          </Tooltip>
        ) : (
          <Tooltip title={accessTooltip}>
            <Button
              type="text"
              danger
              disabled={disableDisabled}
              icon={<LockOutlined rev={undefined} />}
              className="um-refactor__action-icon um-refactor__action-icon--danger"
              onClick={() => onDisable?.(row.member, organization)}
            />
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export default MemberRow
